import { useCallback, useEffect, useRef, useState } from 'react';
import type { VoiceCommandKind, VoiceIntent } from './voiceIntent';
import { intentFromAlternatives } from './voiceIntent';

/**
 * Commande vocale du mode cuisine (US-31, fiabilisée en US-59) : « suivant »,
 * « précédent », « minuteur ». C'est la seule modalité qui n'exige vraiment
 * rien des mains, et donc celle qui répond le plus directement au §4
 * (« s'utilise avec les mains grasses »).
 *
 * Trois précautions d'origine, toujours valables :
 *
 * 1. **Elle se dégrade proprement.** `SpeechRecognition` n'existe ni sur
 *    Firefox ni dans jsdom, et l'autorisation micro peut être refusée. Dans
 *    tous ces cas, le bouton disparaît et les autres modalités — clavier,
 *    balayage, zones de frappe — suffisent.
 * 2. **Elle ne démarre jamais toute seule.** Un micro qui s'ouvre sans qu'on
 *    l'ait demandé est une surprise désagréable, dans une cuisine comme
 *    ailleurs. L'autorisation est demandée au premier clic, une seule fois.
 * 3. **Un refus est définitif pour la session.** On ne redemande pas.
 *
 * ## Pourquoi « ça ne marche pas toujours » (US-59)
 *
 * Trois défauts distincts, tous invisibles à la lecture rapide, et qui se
 * manifestaient exactement comme le disait le rapport d'usage : « de temps en
 * temps la commande minuteur ne fonctionne pas ».
 *
 * ### 1. Le gestionnaire était gelé au premier montage
 *
 * `onresult` était posé **une fois**, à la création de l'instance, et capturait
 * la fonction `onCommand` de ce rendu-là. Or dans `CookMode` cette fonction
 * lit `stepIndex` :
 *
 * ```
 * function toggleTimerForCurrentStep(): void {
 *   const step = steps[stepIndex];   // ← valeur du rendu où le micro a été ouvert
 * ```
 *
 * Conséquence exacte : **« minuteur » armait toujours le minuteur de l'étape
 * où l'on avait ouvert le micro**, et non de l'étape en cours. À l'étape 1 il
 * fonctionnait ; aux suivantes il ne faisait rien de visible — un minuteur
 * tournant déjà pour l'étape 1, la commande sortait sur `return`. « Suivant »
 * et « précédent » survivaient par chance : ils passent par des mises à jour
 * fonctionnelles (`setStepIndex((i) => …)`), qui ne lisent pas la valeur
 * capturée. Le correctif est un `ref` relu à chaque événement.
 *
 * ### 2. Un silence coupait le micro pour de bon
 *
 * Chrome émet `no-speech` après quelques secondes sans parole — c'est le cas
 * normal quand on cuisine sans rien dire. `onerror` faisait alors passer
 * l'état à `idle` ; `onend`, qui suit toujours, relisait cet état, n'y
 * reconnaissait plus « listening » et **ne relançait pas**. Le bouton restait
 * allumé sur un micro éteint : on parlait dans le vide.
 *
 * Les erreurs sont désormais triées : `not-allowed` et `service-not-allowed`
 * sont définitives, `audio-capture` arrête proprement, tout le reste
 * (`no-speech`, `aborted`, `network`) est transitoire et laisse `onend` faire
 * son travail.
 *
 * ### 3. La relance vivait dans un `setState`
 *
 * `recognition.start()` était appelé **dans la fonction de mise à jour** de
 * `setState`. Une fonction de mise à jour doit être pure : React se réserve de
 * l'appeler deux fois (mode strict, rendu concurrent), ce qui démarre la
 * reconnaissance deux fois et lève `InvalidStateError`. L'intention d'écouter
 * est maintenant portée par un `ref` — une intention n'est pas un état
 * d'affichage — et la relance se fait hors rendu, différée de 250 ms : appelée
 * dans `onend` même, Chrome la rejette une fois sur deux.
 *
 * Un budget de relances (8 par minute) évite la boucle folle si le micro
 * refuse durablement : au-delà, on s'arrête et on le dit.
 *
 * ### Et quand l'écran s'éteint
 *
 * Le navigateur coupe le micro quand l'onglet passe en arrière-plan, sans
 * prévenir autrement que par un `onend`. On l'accompagne : `visibilitychange`
 * arrête proprement à la mise en arrière-plan et **relance au retour** si
 * l'intention d'écouter tient toujours. Sans cela, revenir sur la recette
 * après avoir répondu à un message laissait un bouton allumé et un micro mort.
 */

/** Sous-ensemble de l'API `SpeechRecognition` réellement utilisé — le type standard n'est pas dans lib.dom. */
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives?: number;
  start: () => void;
  stop: () => void;
  abort?: () => void;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

function speechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const candidate = (window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  });
  return candidate.SpeechRecognition ?? candidate.webkitSpeechRecognition ?? null;
}

/** La commande vocale est-elle seulement possible sur cet appareil ? */
export function isVoiceSupported(): boolean {
  return speechRecognitionConstructor() !== null;
}

/**
 * Le vocabulaire et son analyse vivent dans `voiceIntent.ts` (US-60) : c'est du
 * texte pur, donc vérifiable sans micro. Ce module ne garde que le branchement
 * au navigateur.
 */
export type { VoiceCommandKind, VoiceIntent } from './voiceIntent';
export { intentFromTranscript } from './voiceIntent';

export type VoiceState = 'unsupported' | 'idle' | 'listening' | 'denied';

export interface VoiceControl {
  state: VoiceState;
  /** Démarre ou arrête l'écoute. Sans effet si l'appareil ne sait pas écouter, ou si l'autorisation a été refusée. */
  toggle: () => void;
  /**
   * Dernière commande comprise, effacée au bout de quelques secondes.
   *
   * Rien ne disait à l'utilisateur qu'il avait été entendu : sur une commande
   * dont l'effet ne se voit pas immédiatement — « minuteur » quand le bandeau
   * est hors du champ de vision — on répétait le mot, ce qui n'a jamais rien
   * amélioré. Le retour est ici pour être affiché.
   */
  derniereCommande: VoiceCommandKind | null;
}

/** Relance différée : appelée depuis `onend` même, Chrome la rejette une fois sur deux. */
const DELAI_RELANCE_MS = 250;
/** Budget de relances, pour ne pas boucler quand le micro refuse durablement. */
const MAX_RELANCES = 8;
const FENETRE_RELANCE_MS = 60_000;
/** Durée d'affichage du retour visuel « compris ». */
const DUREE_RETOUR_MS = 2500;

export function useVoiceCommands(onCommand: (intent: VoiceIntent) => void): VoiceControl {
  const [state, setState] = useState<VoiceState>(() => (isVoiceSupported() ? 'idle' : 'unsupported'));
  const [derniereCommande, setDerniereCommande] = useState<VoiceCommandKind | null>(null);
  // `useRef` et non `useState` : l'instance de reconnaissance est un objet
  // extérieur qu'on **mute** (ses gestionnaires, son démarrage), et un état
  // React ne se mute pas. Elle ne participe d'ailleurs à aucun rendu.
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  /**
   * **L'intention d'écouter**, distincte de l'état affiché. C'est elle que
   * consulte `onend` pour décider de relancer : l'état React n'est pas lisible
   * de façon fiable depuis un gestionnaire d'événement extérieur, et le lire
   * au travers d'un `setState` obligeait à démarrer la reconnaissance dans une
   * fonction de mise à jour — qui doit être pure.
   */
  const souhaiteEcouter = useRef(false);
  const relancesRef = useRef({ essais: 0, depuis: 0 });
  const relanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retourTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Relu à chaque événement : un gestionnaire posé une fois ne doit pas figer la fonction du premier rendu. */
  const onCommandRef = useRef(onCommand);
  useEffect(() => { onCommandRef.current = onCommand; });

  const arreterVraiment = useCallback((): void => {
    souhaiteEcouter.current = false;
    if (relanceTimeoutRef.current !== null) {
      clearTimeout(relanceTimeoutRef.current);
      relanceTimeoutRef.current = null;
    }
    recognitionRef.current?.stop();
  }, []);

  const demarrer = useCallback((): boolean => {
    const instance = recognitionRef.current;
    if (instance === null) return false;
    try {
      instance.start();
      return true;
    } catch {
      // `InvalidStateError` : la reconnaissance tourne déjà. Toute autre
      // erreur ici signifie qu'elle ne démarrera pas — dans les deux cas on
      // ne réessaie pas immédiatement, `onend` reviendra s'il y a lieu.
      return false;
    }
  }, []);

  const relancer = useCallback((): void => {
    if (!souhaiteEcouter.current) return;
    const maintenant = Date.now();
    const budget = relancesRef.current;
    if (maintenant - budget.depuis > FENETRE_RELANCE_MS) {
      budget.depuis = maintenant;
      budget.essais = 0;
    }
    budget.essais += 1;
    if (budget.essais > MAX_RELANCES) {
      // Le micro refuse durablement : mieux vaut un bouton éteint qu'un bouton
      // allumé sur rien.
      souhaiteEcouter.current = false;
      setState('idle');
      return;
    }
    if (relanceTimeoutRef.current !== null) clearTimeout(relanceTimeoutRef.current);
    relanceTimeoutRef.current = setTimeout(() => {
      relanceTimeoutRef.current = null;
      if (!souhaiteEcouter.current) return;
      if (demarrer()) setState('listening');
      else { souhaiteEcouter.current = false; setState('idle'); }
    }, DELAI_RELANCE_MS);
  }, [demarrer]);

  // L'écoute s'arrête dès que l'écran disparaît : un micro qui reste ouvert
  // après qu'on a quitté la cuisine est exactement ce qu'on ne veut pas.
  useEffect(() => {
    return () => {
      souhaiteEcouter.current = false;
      if (relanceTimeoutRef.current !== null) clearTimeout(relanceTimeoutRef.current);
      if (retourTimeoutRef.current !== null) clearTimeout(retourTimeoutRef.current);
      const instance = recognitionRef.current;
      if (instance === null) return;
      instance.onresult = null;
      instance.onerror = null;
      instance.onend = null;
      instance.stop();
    };
  }, []);

  /**
   * Passage en arrière-plan : le navigateur coupe le micro de toute façon. On
   * l'accompagne, et on relance au retour si l'intention tient — sans quoi on
   * revenait sur un bouton allumé et un micro mort.
   */
  useEffect(() => {
    function surVisibilite(): void {
      if (recognitionRef.current === null) return;
      if (document.visibilityState === 'hidden') {
        if (relanceTimeoutRef.current !== null) {
          clearTimeout(relanceTimeoutRef.current);
          relanceTimeoutRef.current = null;
        }
        recognitionRef.current.stop();
        return;
      }
      if (souhaiteEcouter.current) relancer();
    }
    document.addEventListener('visibilitychange', surVisibilite);
    return () => { document.removeEventListener('visibilitychange', surVisibilite); };
  }, [relancer]);

  function attachHandlers(recognition: SpeechRecognitionLike): void {
    recognition.onresult = (event) => {
      const last = event.results[event.results.length - 1];
      // **Toutes** les hypothèses du navigateur, dans l'ordre de confiance
      // (US-60) : le mot juste est souvent en deuxième position quand la
      // cuisine est bruyante. Seule la première était lue jusqu'ici.
      const alternatives: string[] = [];
      for (let i = 0; i < (last?.length ?? 0); i += 1) {
        const transcript = last?.[i]?.transcript;
        if (typeof transcript === 'string') alternatives.push(transcript);
      }
      const intent = intentFromAlternatives(alternatives);
      if (intent === null) return;
      // Relu depuis le `ref` : c'est **le** correctif d'US-59 (cf. en-tête).
      onCommandRef.current(intent);
      setDerniereCommande(intent.kind);
      if (retourTimeoutRef.current !== null) clearTimeout(retourTimeoutRef.current);
      retourTimeoutRef.current = setTimeout(() => {
        retourTimeoutRef.current = null;
        setDerniereCommande(null);
      }, DUREE_RETOUR_MS);
    };
    recognition.onerror = (event) => {
      const erreur = event.error ?? '';
      if (erreur === 'not-allowed' || erreur === 'service-not-allowed') {
        souhaiteEcouter.current = false;
        setState('denied');
        return;
      }
      if (erreur === 'audio-capture') {
        // Aucun micro utilisable : insister n'y changerait rien.
        souhaiteEcouter.current = false;
        setState('idle');
        return;
      }
      // `no-speech`, `aborted`, `network` : transitoires. Ne rien faire est
      // exactement ce qu'il faut — `onend` suit et relance.
    };
    // La reconnaissance s'arrête d'elle-même après un silence : on la relance
    // tant qu'on est censé écouter, sinon la fonction ne marcherait qu'une
    // seule fois.
    recognition.onend = () => {
      if (souhaiteEcouter.current) { relancer(); return; }
      // `onend` suit toujours `onerror` : sans cette garde, un refus
      // d'autorisation repassait aussitôt de `denied` à `idle`, et le bouton
      // réapparaissait pour ne plus jamais rien faire.
      setState((courant) => (courant === 'denied' ? courant : 'idle'));
    };
  }

  function toggle(): void {
    if (state === 'unsupported' || state === 'denied') return;

    if (state === 'listening') {
      arreterVraiment();
      setState('idle');
      return;
    }

    const Constructor = speechRecognitionConstructor();
    if (Constructor === null) { setState('unsupported'); return; }

    relancesRef.current = { essais: 0, depuis: Date.now() };
    souhaiteEcouter.current = true;

    if (recognitionRef.current === null) {
      // Instance créée puis configurée **avant** d'être rangée dans la ref :
      // la règle d'immutabilité de React interdit de muter ce qu'on a déjà lu
      // d'un `useRef`, et on n'en a de toute façon pas besoin — la
      // configuration ne change jamais après coup.
      const instance = new Constructor();
      instance.lang = 'fr-FR';
      instance.continuous = true;
      instance.interimResults = false;
      // Trois hypothèses par énoncé (US-60) : au-delà, on descend dans des
      // transcriptions si improbables qu'elles font surtout du faux positif.
      instance.maxAlternatives = 3;
      attachHandlers(instance);
      recognitionRef.current = instance;
    }

    if (demarrer()) setState('listening');
    else { souhaiteEcouter.current = false; setState('idle'); }
  }

  return { state, toggle, derniereCommande };
}
